import { useCallback, useRef, useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { driver } from "driver.js";
import "driver.js/dist/driver.css";
import api from "../lib/api";

const TOUR_THEME = {
  showProgress: true,
  animate: true,
  smoothScroll: true,
  allowClose: true,
  overlayColor: "rgba(0, 0, 0, 0.65)",
  stagePadding: 8,
  stageRadius: 8,
  popoverClass: "erp-tour-popover",
  progressText: "{{current}} of {{total}}",
  nextBtnText: "Next",
  prevBtnText: "Back",
  doneBtnText: "Done",
};

const TOUR_CONFIGS = {
  dashboard: {
    targetPath: "/",
    title: "Dashboard Tour",
    steps: [
      {
        element: '[data-testid="app-sidebar"]',
        popover: {
          title: "Navigation Sidebar",
          description: "This is your main navigation. All modules are organized by category — Administration, Operations, Finance, and more. Click any item to navigate.",
          side: "right",
          align: "start",
        },
      },
      {
        element: '[data-testid="app-header"]',
        popover: {
          title: "Top Header Bar",
          description: "Access the Financial Year selector, global search (Ctrl+K), pending approvals, theme toggle, and your user profile from here.",
          side: "bottom",
          align: "center",
        },
      },
      {
        element: '[data-testid="global-search-btn"]',
        popover: {
          title: "Global Search",
          description: "Press Ctrl+K or click here to search across all modules — documents, masters, reports, and pages. Supports natural language queries.",
          side: "bottom",
          align: "center",
        },
      },
      {
        element: '[data-testid="fy-selector-btn"]',
        popover: {
          title: "Financial Year Selector",
          description: "Switch between financial years. All documents and reports automatically filter by the selected FY. Current: 2025-26.",
          side: "bottom",
          align: "center",
        },
      },
      {
        element: '[data-testid="approvals-btn"]',
        popover: {
          title: "Pending Approvals",
          description: "Quick access to documents waiting for your approval. The red badge shows the count of pending items.",
          side: "bottom",
          align: "center",
        },
      },
      {
        element: '[data-testid="user-menu-btn"]',
        popover: {
          title: "User Menu",
          description: "Access your profile, training guide, and logout option. Click to see more options.",
          side: "bottom",
          align: "end",
        },
      },
    ],
  },

  sales: {
    targetPath: "/sales",
    title: "Sales Order Tour",
    steps: [
      {
        element: '[data-testid="sales-page"]',
        popover: {
          title: "Sales Orders Module",
          description: "This is where you manage all your Sales Orders. Track orders from draft to dispatch with full lifecycle management.",
          side: "bottom",
          align: "start",
        },
      },
      {
        element: '[data-testid="sales-tabs"]',
        popover: {
          title: "Sales Tabs",
          description: "Switch between Sales Orders, Dispatch Notes, and Analytics views. Each tab gives you a different perspective on your sales data.",
          side: "bottom",
          align: "start",
        },
      },
      {
        element: '[data-testid="create-so-btn"]',
        popover: {
          title: "Create New Sales Order",
          description: "Click here to create a new Sales Order. Select customer, add pipe sizes with grades, quantities, and rates. Set delivery dates and tolerance %.",
          side: "bottom",
          align: "end",
        },
      },
      {
        element: '[data-testid="view-switcher"]',
        popover: {
          title: "Multi-View System",
          description: "Switch between List, Kanban, Calendar, Timeline, and Pivot Table views. Each view gives you a unique way to analyze your sales data.",
          side: "bottom",
          align: "center",
        },
      },
      {
        element: '[data-testid="so-table"]',
        popover: {
          title: "Sales Order Table",
          description: "View all sales orders with status, customer, amount, and delivery details. Click any row to see full details, dispatch, and generate invoices.",
          side: "top",
          align: "center",
        },
      },
    ],
  },

  purchase: {
    targetPath: "/purchase",
    title: "Purchase Order Tour",
    steps: [
      {
        element: '[data-testid="purchase-page"], .space-y-5',
        popover: {
          title: "Purchase Orders Module",
          description: "Manage all purchase orders for raw materials — billets, plates, spares. Track from creation to delivery with full GRN management.",
          side: "bottom",
          align: "start",
        },
      },
      {
        element: '[data-testid="create-po-btn"], button:has(> .lucide-plus)',
        popover: {
          title: "Create Purchase Order",
          description: "Click '+ New PO' to create a purchase order. Select supplier, add line items, set quantities & rates. PO numbers are auto-generated.",
          side: "bottom",
          align: "end",
        },
      },
      {
        element: '[data-testid="view-switcher"]',
        popover: {
          title: "View Options",
          description: "Switch between List, Kanban, Calendar, Timeline, and Pivot views to analyze your purchase data from different angles.",
          side: "bottom",
          align: "center",
        },
      },
    ],
  },

  tasks: {
    targetPath: "/tasks",
    title: "Task Management Tour",
    steps: [
      {
        element: '[data-testid="tasks-page"]',
        popover: {
          title: "Task Management",
          description: "Create, assign, and track tasks across your organization. Support for priorities, departments, due dates, and multiple view modes.",
          side: "bottom",
          align: "start",
        },
      },
      {
        element: '[data-testid="create-task-btn"]',
        popover: {
          title: "Create New Task",
          description: "Click to create a new task. Set title, description, priority (P0-P3), department, assignees, and due date.",
          side: "bottom",
          align: "end",
        },
      },
      {
        element: '[data-testid="task-tabs"]',
        popover: {
          title: "Dashboard & Task Views",
          description: "Switch between Dashboard (analytics overview) and Tasks (list with filters). Dashboard shows status distribution, priority breakdown, and overdue counts.",
          side: "bottom",
          align: "start",
        },
      },
      {
        element: '[data-testid="task-filters"]',
        popover: {
          title: "Status Filters",
          description: "Quick-filter tasks by status: All, Open, In Progress, Under Review, Completed, and Blocked. Click any filter to instantly update the view.",
          side: "bottom",
          align: "start",
        },
      },
      {
        element: '[data-testid="view-switcher"]',
        popover: {
          title: "Multi-View System",
          description: "Switch to Kanban view for drag-and-drop status changes! Also available: Calendar, Timeline, and Pivot Table views.",
          side: "bottom",
          align: "center",
        },
      },
    ],
  },

  production: {
    targetPath: "/production",
    title: "Production Tour",
    steps: [
      {
        popover: {
          title: "Production Module",
          description: "Manage Work Orders, track production entries per shift, and create bundles of finished pipes. Monitor yield and scrap automatically.",
          side: "bottom",
          align: "start",
        },
      },
      {
        element: '[data-testid="view-switcher"]',
        popover: {
          title: "Production Views",
          description: "Use Kanban to see work orders by status, Calendar to view schedule, Timeline for Gantt-chart view, or Pivot Table for analysis.",
          side: "bottom",
          align: "center",
        },
      },
    ],
  },

  settings: {
    targetPath: "/settings",
    title: "System Settings Tour",
    steps: [
      {
        popover: {
          title: "System Settings",
          description: "Configure your ERP system — master data, users, production settings, status flows, document sequences, and more. This is the admin control center.",
          side: "bottom",
          align: "start",
        },
      },
    ],
  },

  crm: {
    targetPath: "/crm",
    title: "CRM & Lead Management Tour",
    steps: [
      {
        popover: {
          title: "CRM & Leads",
          description: "Manage your sales pipeline with a visual Kanban board. Track leads from first contact to deal closure. Drag cards between stages to update status.",
          side: "bottom",
          align: "start",
        },
      },
    ],
  },

  inventory: {
    targetPath: "/inventory",
    title: "Inventory Tour",
    steps: [
      {
        popover: {
          title: "Inventory Management",
          description: "View all stock categorized: Raw Material, WIP, FG Prime, B-Grade, Short Piece, and Scrap. Click category cards to filter. Use stock adjustments for corrections.",
          side: "bottom",
          align: "start",
        },
      },
    ],
  },

  finance: {
    targetPath: "/finance",
    title: "Finance & Invoicing Tour",
    steps: [
      {
        popover: {
          title: "Finance & Invoicing",
          description: "Generate GST-compliant tax invoices with auto-calculated CGST/SGST/IGST. Record payments, track outstanding amounts, and download professional PDFs.",
          side: "bottom",
          align: "start",
        },
      },
    ],
  },

  quality: {
    targetPath: "/quality",
    title: "Quality Control Tour",
    steps: [
      {
        popover: {
          title: "Quality Control",
          description: "Create QC inspections for received and produced material. Mark Pass/Fail/Hold. Generate Test Certificates and verify MTCs from suppliers.",
          side: "bottom",
          align: "start",
        },
      },
    ],
  },
};

export const TOUR_META = Object.fromEntries(
  Object.entries(TOUR_CONFIGS).map(([id, cfg]) => [id, { title: cfg.title, steps: cfg.steps.length }])
);

export function useProductTour() {
  const navigate = useNavigate();
  const location = useLocation();
  const driverRef = useRef(null);
  const [completedTours, setCompletedTours] = useState([]);
  const [loading, setLoading] = useState(true);

  // Load progress on mount
  useEffect(() => {
    let mounted = true;
    api.get("/training/tour-progress")
      .then(res => {
        if (mounted) setCompletedTours(res.data.completed_tours || []);
      })
      .catch(() => {})
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, []);

  const markComplete = useCallback(async (tourId) => {
    try {
      await api.post("/training/tour-complete", { tour_id: tourId });
      setCompletedTours(prev => prev.includes(tourId) ? prev : [...prev, tourId]);
    } catch {
      // silent
    }
  }, []);

  const startTour = useCallback((tourId) => {
    const config = TOUR_CONFIGS[tourId];
    if (!config) return;

    if (driverRef.current) {
      driverRef.current.destroy();
    }

    const launchDriver = () => {
      setTimeout(() => {
        const validSteps = config.steps.filter((step) => {
          if (!step.element) return true;
          return document.querySelector(step.element);
        });

        if (validSteps.length === 0) {
          validSteps.push({
            popover: {
              title: config.title,
              description: "Navigate to this module to explore its features. Use the sidebar to get started.",
            },
          });
        }

        const driverObj = driver({
          ...TOUR_THEME,
          steps: validSteps,
          onDestroyed: () => {
            driverRef.current = null;
          },
          onDestroyStarted: () => {
            // Mark tour as complete when user finishes (reaches last step or closes)
            if (driverObj.isLastStep() || !driverObj.hasNextStep()) {
              markComplete(tourId);
            }
            driverObj.destroy();
          },
        });

        driverRef.current = driverObj;
        driverObj.drive();
      }, 600);
    };

    if (config.targetPath && location.pathname !== config.targetPath) {
      navigate(config.targetPath);
      setTimeout(launchDriver, 800);
    } else {
      launchDriver();
    }
  }, [navigate, location.pathname, markComplete]);

  const stopTour = useCallback(() => {
    if (driverRef.current) {
      driverRef.current.destroy();
      driverRef.current = null;
    }
  }, []);

  return {
    startTour,
    stopTour,
    completedTours,
    isCompleted: (tourId) => completedTours.includes(tourId),
    totalTours: Object.keys(TOUR_CONFIGS).length,
    completionPercent: Math.round((completedTours.length / Object.keys(TOUR_CONFIGS).length) * 100),
    loading,
    availableTours: Object.keys(TOUR_CONFIGS),
  };
}
